import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { loadWorkflows } from '../../store/project/projectSlice';
import { useTranslation } from 'react-i18next';

/**
 * 워크플로 목록이 **비어 보이는 이유**를 화면에 말해 주는 조각.
 *
 * 왜 필요한가: 목록 읽기가 실패해도 스토어에는 빈 배열이 남는다. 그래서 **불러오는 중 · 실패 ·
 * 정말 0건** 이 셋이 화면에서 똑같이 "아무것도 없음" 으로 보였다. 2026-09-15 신고 "샘플프로젝트를
 * 선택했는데 워크플로가 안보임" 이 바로 이것이다 — 서버에는 25건이 멀쩡히 있었다.
 *
 * 세 화면(대시보드 · 실행 탭 · 디자이너 탐색기)이 같은 맹점을 각자 갖고 있었으므로 한 조각으로 둔다.
 * **'정말 0건' 일 때 할 말(`emptyHint`)까지 여기서 받는다** — 그래야 부르는 쪽이 상태를 따로 읽지
 * 않고, 새 목록 화면이 생겨도 이것만 끼우면 같은 실수를 되풀이하지 않는다.
 *
 * 실패에는 **다시 시도**를 준다. 원인이 대개 한때의 것(서버 재기동·게이트웨이 시간초과)이라 한 번 더
 * 누르면 풀리는데, 손잡이가 없으면 사용자는 새로고침·재로그인을 헤맨다.
 */
interface Props {
  /** 좁은 자리(디자이너 탐색기)용 — 글씨와 여백을 줄인다 */
  compact?: boolean;
  /** 읽기에 성공했고 정말 0건일 때 보일 것. 주지 않으면 아무것도 보이지 않는다 */
  emptyHint?: React.ReactNode;
}

const WorkflowLoadNotice: React.FC<Props> = ({ compact, emptyHint }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const status = useSelector((s: RootState) => s.project.workflowsStatus);
  const message = useSelector((s: RootState) => s.project.workflowsError);
  const count = useSelector(
    (s: RootState) => s.project.workflows.filter((w) => w.projectId === s.project.selectedProjectId).length,
  );

  if (status === 'loading') {
    return (
      <div className={compact ? 'py-1 text-xs text-gray-400' : 'py-3 text-center text-sm text-gray-500'}>
        {t('layout:load.loading')}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        role="alert"
        className={
          compact
            ? 'my-1 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[0.6875rem] text-red-700'
            : 'my-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
        }
      >
        <div>{t('layout:load.failed')}{message ? `: ${message}` : '.'}</div>
        <div className={compact ? 'mt-1' : 'mt-1.5'}>
          <button
            type="button"
            className="rounded border border-red-300 bg-white px-2 py-0.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
            onClick={() => projectId && dispatch(loadWorkflows(projectId))}
            disabled={!projectId}
          >
            {t('layout:load.retry')}
          </button>
          <span className={compact ? 'ml-2 text-red-500' : 'ml-2 text-xs text-red-500'}>
            {t('layout:load.notDeleted')}
          </span>
        </div>
      </div>
    );
  }

  if (count === 0 && emptyHint) return <>{emptyHint}</>;
  return null;
};

export default WorkflowLoadNotice;
